/**
 * TTS Service
 * Tries backend OpenAI TTS first (supports all languages including Urdu/Hindi).
 * Falls back to browser speechSynthesis for English if backend is unavailable.
 */

import { apiClient } from './api';

export type TTSVoice = 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer';

interface TTSRequest {
  text: string;
  voice?: TTSVoice;
  speed?: number;
}

// Module-level abort controller — cancels any in-flight TTS request
// when a new speak() call is made (prevents double audio)
let currentAbortController: AbortController | null = null;

/**
 * Strip emojis from text so TTS doesn't read them as descriptions
 * e.g. prevents "smiling face with smiling eyes" being spoken
 */
function stripEmojis(text: string): string {
  return text
    .replace(/[\u{1F600}-\u{1F64F}]/gu, '') // Emoticons
    .replace(/[\u{1F300}-\u{1F5FF}]/gu, '') // Misc symbols & pictographs
    .replace(/[\u{1F680}-\u{1F6FF}]/gu, '') // Transport & map
    .replace(/[\u{1F1E0}-\u{1F1FF}]/gu, '') // Flags
    .replace(/[\u{2600}-\u{26FF}]/gu, '')   // Misc symbols
    .replace(/[\u{2700}-\u{27BF}]/gu, '')   // Dingbats
    .replace(/[\u{FE00}-\u{FE0F}]/gu, '')   // Variation selectors
    .replace(/[\u{1F900}-\u{1F9FF}]/gu, '') // Supplemental symbols
    .replace(/[\u{1FA00}-\u{1FA6F}]/gu, '') // Chess symbols
    .replace(/[\u{1FA70}-\u{1FAFF}]/gu, '') // Symbols extended-A
    .replace(/[\u{200D}]/gu, '')             // Zero width joiner
    .replace(/\s{2,}/g, ' ')                // Collapse multiple spaces
    .trim();
}

/**
 * Convert text to speech audio via backend OpenAI TTS API
 * Returns an audio Blob (MP3)
 */
async function synthesize(options: TTSRequest, signal?: AbortSignal): Promise<Blob> {
  const response = await apiClient.post('/tts', options, {
    responseType: 'blob',
    timeout: 15000,
    signal,
  });
  return response.data;
}

/**
 * Play text using browser speechSynthesis (fallback for English)
 * Returns a fake Audio-like object with pause() for consistency
 */
function speakWithBrowser(text: string): HTMLAudioElement {
  if (!('speechSynthesis' in window)) throw new Error('No TTS available');

  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.rate = 0.9;
  utterance.pitch = 1.1;

  // Create a dummy audio element to match the interface
  const dummy = new Audio();
  const origPause = dummy.pause.bind(dummy);

  // Override pause to also cancel speechSynthesis
  dummy.pause = () => {
    window.speechSynthesis.cancel();
    origPause();
  };

  utterance.onend = () => dummy.dispatchEvent(new Event('ended'));
  utterance.onerror = () => dummy.dispatchEvent(new Event('error'));

  window.speechSynthesis.speak(utterance);
  return dummy;
}

/**
 * Cancel any currently in-flight TTS request and stop playback
 */
function cancel() {
  if (currentAbortController) {
    currentAbortController.abort();
    currentAbortController = null;
  }
  window.speechSynthesis?.cancel();
}

/**
 * Play text as speech audio
 * Tries backend OpenAI TTS first, falls back to browser speechSynthesis
 * Returns the Audio element for control (pause/stop)
 *
 * Automatically cancels any previous in-flight request (prevents double audio)
 */
async function speak(text: string, voice?: TTSVoice, speed?: number): Promise<HTMLAudioElement> {
  // Cancel any previous in-flight TTS request
  cancel();

  const cleanText = stripEmojis(text);
  if (!cleanText) throw new Error('No speakable text');

  const abortController = new AbortController();
  currentAbortController = abortController;

  // Try backend OpenAI TTS first
  try {
    const blob = await synthesize({ text: cleanText, voice, speed }, abortController.signal);

    // Check if this request was cancelled while we were waiting
    if (abortController.signal.aborted) throw new Error('Aborted');

    // Verify we got actual audio (not an error HTML page)
    if (blob.size > 0 && (blob.type.includes('audio') || blob.type === 'application/octet-stream')) {
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.addEventListener('ended', () => URL.revokeObjectURL(url));
      audio.addEventListener('error', () => URL.revokeObjectURL(url));
      await audio.play();
      return audio;
    }
  } catch (err) {
    // If aborted, don't fall through to browser TTS
    if (abortController.signal.aborted) throw new Error('TTS cancelled');
    // Backend unavailable - fall through to browser TTS
  }

  // Double-check not cancelled before fallback
  if (abortController.signal.aborted) throw new Error('TTS cancelled');

  // Fallback: browser speechSynthesis
  return speakWithBrowser(cleanText);
}

const ttsService = {
  synthesize,
  speak,
  cancel,
};

export default ttsService;
